/**
 * Price Range Slider Component
 * Handles dual-range price filtering with visual slider interface
 */

class PriceRangeSlider {
  constructor(container) {
    this.container = container;
    this.minSlider = container.querySelector('.price-slider-min');
    this.maxSlider = container.querySelector('.price-slider-max');
    this.rangeTrack = container.querySelector('.price-slider-range');
    this.minValueDisplay = container.querySelector('.price-slider-min-value');
    this.maxValueDisplay = container.querySelector('.price-slider-max-value');
    
    if (!this.minSlider || !this.maxSlider || !this.rangeTrack) {
      return;
    }

    // Get target inputs for form submission
    this.minInput = document.getElementById(this.minSlider.dataset.targetInput);
    this.maxInput = document.getElementById(this.maxSlider.dataset.targetInput);
    
    // Get currency symbol
    this.currencySymbol = this.minSlider.dataset.currencySymbol || '$';
    this.currencyCode = this.minSlider.dataset.currencyCode || '';
    this.init();
  }

  init() {
    // Values/display/track come from the slider position, but "is this filter active"
    // must come from the server-rendered disabled state — recomputing it from slider
    // position on load caused the price filter to be sent on every request (see: any
    // filter checkbox click was submitting min_price/max_price too).
    this.syncValuesFromSliders();
    this.updateDisplay();
    this.updateRangeTrack();
    this.bindEvents();
    this.container.dataset.initialized = 'true';
  }

  bindEvents() {
    this.minSlider.addEventListener('input', () => this.handleMinChange());
    this.maxSlider.addEventListener('input', () => this.handleMaxChange());
  }

  handleMinChange() {
    this.preventCrossover('min');
    this.syncInputsFromSliders();
    this.updateDisplay();
    this.updateRangeTrack();
  }

  handleMaxChange() {
    this.preventCrossover('max');
    this.syncInputsFromSliders();
    this.updateDisplay();
    this.updateRangeTrack();
  }

  preventCrossover(activeSlider) {
    const minValue = this.getNumericValue(this.minSlider.value);
    const maxValue = this.getNumericValue(this.maxSlider.value);
    const step = this.getNumericValue(this.minSlider.step) || 1;

    if (minValue >= maxValue) {
      if (activeSlider === 'min') {
        const nextValue = Math.min(minValue + step, this.getNumericValue(this.maxSlider.max));
        this.maxSlider.value = nextValue;
      } else {
        const nextValue = Math.max(maxValue - step, this.getNumericValue(this.minSlider.min));
        this.minSlider.value = nextValue;
      }
    }
  }

  updateDisplay() {
    const minValue = this.getNumericValue(this.minSlider.value);
    const maxValue = this.getNumericValue(this.maxSlider.value);
    
    // Format as currency
    const minFormatted = this.formatCurrency(minValue);
    const maxFormatted = this.formatCurrency(maxValue);
    
    if (this.minValueDisplay) {
      this.minValueDisplay.textContent = minFormatted;
    }
    
    if (this.maxValueDisplay) {
      this.maxValueDisplay.textContent = maxFormatted;
    }
  }

  updateRangeTrack() {
    const min = this.getNumericValue(this.minSlider.min);
    const max = this.getNumericValue(this.minSlider.max);
    const minValue = this.getNumericValue(this.minSlider.value);
    const maxValue = this.getNumericValue(this.maxSlider.value);

    if (max <= min) {
      return;
    }

    const minPercent = ((minValue - min) / (max - min)) * 100;
    const maxPercent = ((maxValue - min) / (max - min)) * 100;

    if (this.rangeTrack) {
      this.rangeTrack.style.left = minPercent + '%';
      this.rangeTrack.style.width = (maxPercent - minPercent) + '%';
    }
  }

  syncInputsFromSliders() {
    const minVal = this.getNumericValue(this.minSlider.value);
    const maxVal = this.getNumericValue(this.maxSlider.value);
    const rangeMin = this.getNumericValue(this.minSlider.min);
    const rangeMax = this.getNumericValue(this.maxSlider.max);
    const isFiltered = minVal > rangeMin || maxVal < rangeMax;

    if (this.minInput) {
      this.minInput.value = this.minSlider.value;
      this.minInput.disabled = !isFiltered;
    }

    if (this.maxInput) {
      this.maxInput.value = this.maxSlider.value;
      this.maxInput.disabled = !isFiltered;
    }
  }

  // Used on init only: keeps the server-rendered disabled state (based on the actual
  // active filter params) intact, and just mirrors the slider's current value into the input.
  syncValuesFromSliders() {
    if (this.minInput) {
      this.minInput.value = this.minSlider.value;
    }

    if (this.maxInput) {
      this.maxInput.value = this.maxSlider.value;
    }
  }

  getNumericValue(value) {
    const parsedValue = Number.parseFloat(value);
    return Number.isNaN(parsedValue) ? 0 : parsedValue;
  }

  formatCurrency(value) {
    if (this.currencyCode && typeof Intl !== 'undefined') {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }

    return this.currencySymbol + value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}

// Initialize all price range sliders
document.addEventListener('DOMContentLoaded', () => {
  const sliderContainers = document.querySelectorAll('.price-slider-container');
  sliderContainers.forEach(container => {
    if (!container.dataset.initialized) {
      new PriceRangeSlider(container);
    }
  });
});

// Re-initialize after AJAX filter content replacement
document.addEventListener('filtersReplaced', () => {
  document.querySelectorAll('.price-slider-container').forEach(container => {
    delete container.dataset.initialized;
    new PriceRangeSlider(container);
  });
});

// Re-initialize when fly-out panel opens (for dynamic content)
document.addEventListener('click', (e) => {
  if (e.target.matches('.js-fly-out-panel-toggle')) {
    // Small delay to ensure panel content is rendered
    setTimeout(() => {
      const sliderContainers = document.querySelectorAll('.price-slider-container');
      sliderContainers.forEach(container => {
        // Check if already initialized
        if (!container.dataset.initialized) {
          new PriceRangeSlider(container);
          container.dataset.initialized = 'true';
        }
      });
    }, 100);
  }
});

export default PriceRangeSlider;
